import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from 'react';
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  BookOpen,
  ChevronLeft,
  ChevronRight,
  KeyRound,
  Pencil,
  Plus,
  RotateCcw,
  SlidersHorizontal,
  Trash2,
  Wrench,
} from 'lucide-react';
import { ApiError } from '@/lib/api';
import { isAdminRole, useAuthStore } from '@/lib/auth-store';
import {
  DeleteCaptchaField,
  useDeleteCaptcha,
} from '@/components/ui/delete-captcha';
import {
  createEmployeeApi,
  getEmployeeCoursesApi,
  listAvatarPresetsApi,
  listDepartmentsApi,
  listEmployeesApi,
  resetEmployeePasswordApi,
  resetEmployeeTrainingApi,
  setEmployeeCoursesApi,
  softDeleteEmployeeApi,
  trainingRecoveryApplyApi,
  trainingRecoveryDryRunApi,
  updateEmployeeApi,
} from '@/lib/employees-api';
import {
  describeRecoverySummary,
  hasActionableRecovery,
  statusDistributionRows,
} from '@/lib/training-recovery';
import {
  buildEmployeeListQuery,
  EMPLOYEE_DEFAULT_PAGE_SIZE,
  EMPLOYEE_PAGE_SIZE_OPTIONS,
  resolveVisibleColumns,
  toggleColumn as toggleColumnHelper,
} from '@/lib/employees-filters';
import jzLogo from '@/assets/jzlogo.png';
import {
  EMPLOYEE_FILTER_STATUSES,
  EMPLOYEE_STATUSES,
  isCancelled,
  toCreatePayload,
  toUpdatePayload,
  validateEmployeeForm,
  type EmployeeFormValues,
} from '@/lib/employee-form';
import { getHrConfigApi } from '@/lib/settings-api';
import { hrRoleLabel, normalizeHrRoleOptions, withCurrentPosition } from '@/lib/hr-config';
import type {
  AdminEmployeeView,
  EmployeeStatus,
  HrRoleOption,
  Role,
  TrainingRecoveryApplyResult,
} from '@/lib/types';
import { Card } from '@/components/ui/card';
import { Modal } from '@/components/ui/modal';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/empty-state';

// Issue #172：管理员 Web 员工管理 = 真实 /admin/employees* 接入（增删改 / 重置密码 /
// 重置学习进度）。所有写操作走真实接口，错误经 ApiError（含 HTTP 状态）展示，绝不
// 使用前端假状态。普通员工（非 ADMIN+）被拒，不渲染管理数据。

const PRIMARY_BTN =
  'inline-flex items-center gap-2 rounded-md bg-brand-500 px-3 py-1.5 text-sm font-medium text-white transition hover:bg-brand-600 disabled:opacity-60';
const SECONDARY_BTN =
  'inline-flex items-center gap-2 rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-xs text-slate-600 transition hover:bg-slate-50 disabled:opacity-60';
const DANGER_BTN =
  'inline-flex items-center gap-2 rounded-md bg-rose-600 px-3 py-1.5 text-sm font-medium text-white transition hover:bg-rose-700 disabled:opacity-60';
const INPUT =
  'w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100';

const ROLE_LABEL: Record<Role, string> = {
  SUPER_ADMIN: '超级管理员',
  ADMIN: '管理员',
  MANAGER: '主管',
  STAFF: '员工',
};
const STATUS_LABEL: Record<EmployeeStatus, string> = {
  ACTIVE: '在职',
  INACTIVE: '停用',
  SUSPENDED: '暂停',
  // Issue #6：员工自助注销的终态。注销不写 deletedAt，员工仍留在列表里，
  // 只是明确标注为「账户已注销」（与「停用」区分：后者是管理员操作、可恢复）。
  CANCELLED: '账户已注销',
};
const STATUS_TONE: Record<EmployeeStatus, string> = {
  ACTIVE: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  INACTIVE: 'bg-slate-100 text-slate-600 border-slate-200',
  SUSPENDED: 'bg-amber-50 text-amber-700 border-amber-200',
  CANCELLED: 'bg-rose-50 text-rose-700 border-rose-200',
};

type ModalState =
  | { kind: 'create' }
  | { kind: 'edit'; employee: AdminEmployeeView }
  | { kind: 'delete'; employee: AdminEmployeeView }
  | { kind: 'reset-password'; employee: AdminEmployeeView }
  | { kind: 'reset-training'; employee: AdminEmployeeView }
  | { kind: 'assign-courses'; employee: AdminEmployeeView }
  | { kind: 'training-recovery'; employee: AdminEmployeeView }
  | null;

function errMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    return err.status > 0 ? `${err.message}（HTTP ${err.status}）` : err.message;
  }
  return err instanceof Error ? err.message : fallback;
}

// Issue #183/#184：默认员工头像优先用**租户配置的默认头像**（avatarUrl 为空时回退到
// configuredDefaultUrl），其次才是随包金专 logo（配置项本身加载失败时的最终兜底），
// 不再显示空圆圈或姓名首字占位。
function Avatar({
  url,
  name,
  fallbackUrl,
}: {
  url: string | null;
  name: string;
  fallbackUrl?: string;
}) {
  const [broken, setBroken] = useState(false);
  const effective = url || fallbackUrl || null;
  const src = effective && !broken ? effective : jzLogo;
  return (
    <img
      src={src}
      alt={name}
      className="h-9 w-9 rounded-full border border-slate-200 bg-white object-cover"
      onError={() => setBroken(true)}
    />
  );
}

// 预设头像缩略图：加载失败时回退到金专 logo，避免破图 / 空圆圈（Issue #184）。
function AvatarThumb({ src }: { src: string }) {
  const [broken, setBroken] = useState(false);
  return (
    <img
      src={broken ? jzLogo : src}
      alt="预设头像"
      className="h-full w-full object-cover"
      onError={() => setBroken(true)}
    />
  );
}

const EMPLOYEE_COLUMN_STORAGE_KEY = 'jzcollege.employees.visibleColumns.v1';

/** 读取本地持久化的可见列；缺失 / 解析失败返回 null（由 resolveVisibleColumns 回退默认集）。 */
function readStoredEmployeeColumns(): string[] | null {
  try {
    const raw = localStorage.getItem(EMPLOYEE_COLUMN_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed)
      ? parsed.filter((c): c is string => typeof c === 'string')
      : null;
  } catch {
    return null;
  }
}

/** 角色筛选可选项（含 SUPER_ADMIN，仅用于过滤，不用于建号）。 */
const FILTER_ROLES = Object.keys(ROLE_LABEL) as Role[];

/** 单元格渲染：列 key → 内容。列是逻辑列（employee 聚合头像+姓名+工号），非字段直投。 */
function EmployeeCell({
  col,
  emp,
  fallbackAvatar,
  roleOptions,
}: {
  col: string;
  emp: AdminEmployeeView;
  fallbackAvatar?: string;
  roleOptions?: HrRoleOption[];
}) {
  switch (col) {
    case 'employee':
      return (
        <div className="flex items-center gap-2.5">
          <Avatar url={emp.avatarUrl} name={emp.name} fallbackUrl={fallbackAvatar} />
          <div>
            <div className="font-medium text-slate-900">{emp.name}</div>
            <div className="text-xs text-slate-400">{emp.username ? `@${emp.username}` : '—'}</div>
          </div>
        </div>
      );
    case 'phoneMasked':
      return <span className="text-slate-600">{emp.phoneMasked}</span>;
    case 'deptName':
      return <span className="text-slate-600">{emp.deptName ?? '—'}</span>;
    case 'jobTitle':
      return <span className="text-slate-600">{emp.jobTitle ?? '—'}</span>;
    case 'role':
      return <span className="text-slate-600">{hrRoleLabel(emp.role, roleOptions) ?? emp.role}</span>;
    case 'status':
      return (
        <span
          className={`inline-flex rounded-full border px-2 py-0.5 text-xs ${STATUS_TONE[emp.status] ?? ''}`}
        >
          {STATUS_LABEL[emp.status] ?? emp.status}
        </span>
      );
    default:
      return <span className="text-slate-400">—</span>;
  }
}

export function EmployeesPage() {
  const currentUser = useAuthStore((s) => s.user);
  const isAdmin = isAdminRole(currentUser?.role);
  const queryClient = useQueryClient();
  const [modal, setModal] = useState<ModalState>(null);
  const [columnPanelOpen, setColumnPanelOpen] = useState(false);

  // 高级搜索 + 分页（受控）。pendingKeyword 仅在「查询」时落到 keyword，避免逐字打接口。
  const [pendingKeyword, setPendingKeyword] = useState('');
  const [keyword, setKeyword] = useState('');
  const [deptId, setDeptId] = useState('');
  const [role, setRole] = useState<Role | ''>('');
  const [status, setStatus] = useState<EmployeeStatus | ''>('');
  const [sortBy, setSortBy] = useState<'createdAt' | 'name' | 'lastLoginAt'>('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(EMPLOYEE_DEFAULT_PAGE_SIZE);

  // 列可见性（浏览器持久化，但永远与后端列白名单求交集 + 强制不可隐藏列）。
  const [visibleColumns, setVisibleColumns] = useState<string[] | null>(null);

  const listQuery = useMemo(
    () =>
      buildEmployeeListQuery({
        keyword,
        deptId,
        role,
        status,
        sortBy,
        sortOrder,
        page,
        pageSize,
      }),
    [keyword, deptId, role, status, sortBy, sortOrder, page, pageSize],
  );

  const employeesQuery = useQuery({
    queryKey: ['admin', 'employees', listQuery],
    enabled: isAdmin,
    queryFn: () => listEmployeesApi(listQuery),
    placeholderData: keepPreviousData,
  });
  // Issue #183 返修（P0-3）：列表头像 null 回退到**租户配置的默认头像**（而非随包 logo）。
  const presetsQuery = useQuery({
    queryKey: ['admin', 'avatar-presets'],
    enabled: isAdmin,
    queryFn: listAvatarPresetsApi,
    staleTime: 60 * 60 * 1000,
  });
  const configuredDefaultAvatar = presetsQuery.data?.configuredDefaultUrl;

  // 部门筛选下拉数据。
  const departmentsQuery = useQuery({
    queryKey: ['admin', 'departments'],
    enabled: isAdmin,
    queryFn: listDepartmentsApi,
    staleTime: 5 * 60 * 1000,
  });
  const departments = departmentsQuery.data ?? [];
  const hrConfigQuery = useQuery({
    queryKey: ['system', 'hr-config'],
    enabled: isAdmin,
    queryFn: getHrConfigApi,
    staleTime: 5 * 60 * 1000,
  });
  const roleOptions = useMemo(
    () => normalizeHrRoleOptions(hrConfigQuery.data?.roles),
    [hrConfigQuery.data?.roles],
  );

  // useMemo 稳定引用，避免作为下方 useEffect 依赖时每帧变化（react-hooks/exhaustive-deps）。
  const columnsMeta = useMemo(
    () => employeesQuery.data?.columns ?? [],
    [employeesQuery.data],
  );

  // 列白名单到达后初始化可见列：优先持久化值，与白名单求交集 + 强制不可隐藏列。
  useEffect(() => {
    if (columnsMeta.length === 0 || visibleColumns !== null) return;
    setVisibleColumns(resolveVisibleColumns(readStoredEmployeeColumns(), columnsMeta));
  }, [columnsMeta, visibleColumns]);

  useEffect(() => {
    if (visibleColumns) {
      try {
        localStorage.setItem(EMPLOYEE_COLUMN_STORAGE_KEY, JSON.stringify(visibleColumns));
      } catch {
        /* 持久化失败不阻塞功能 */
      }
    }
  }, [visibleColumns]);

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['admin', 'employees'] });
  const closeModal = () => setModal(null);

  if (!isAdmin) {
    return (
      <div className="flex flex-col gap-6">
        <Header />
        <Card title="员工管理">
          <EmptyState
            title="当前角色无权管理员工"
            description="员工管理仅 ADMIN 及以上角色可用。普通员工请使用移动端学习与质检功能。"
          />
        </Card>
      </div>
    );
  }

  const data = employeesQuery.data;
  const employees = data?.items ?? [];
  const pagination = data?.pagination;
  // 可见列：状态就绪后用状态，否则即时从持久化 + 白名单求解（保证首帧也只渲染白名单列）。
  const effectiveVisible =
    visibleColumns ?? resolveVisibleColumns(readStoredEmployeeColumns(), columnsMeta);
  // 数据列 = 可见列去掉 actions（actions 始终单独渲染在行尾，恒可见不可隐藏）。
  const dataColumns = effectiveVisible.filter((c) => c !== 'actions');
  const colLabel = (col: string) => columnsMeta.find((c) => c.key === col)?.label ?? col;

  function applyFilters(e: FormEvent) {
    e.preventDefault();
    setKeyword(pendingKeyword.trim());
    setPage(1);
  }
  function resetFilters() {
    setPendingKeyword('');
    setKeyword('');
    setDeptId('');
    setRole('');
    setStatus('');
    setSortBy('createdAt');
    setSortOrder('desc');
    setPage(1);
  }
  function toggleCol(col: string) {
    setVisibleColumns((prev) => toggleColumnHelper(prev ?? effectiveVisible, col, columnsMeta));
  }

  return (
    <div className="flex flex-col gap-6">
      <Header />
      <Card
        title="员工目录"
        description="按关键词 / 部门 / 角色 / 状态高级检索，支持列隐藏与分页（默认 20 条/页）。管理员可新增 / 编辑 / 软删除员工，重置密码（随机生成一次性临时密码，仅展示一次，首次登录强制改密），并代员工重置学习进度。"
        action={
          <div className="flex items-center gap-2">
            <button
              type="button"
              className={SECONDARY_BTN}
              onClick={() => setColumnPanelOpen((v) => !v)}
            >
              <SlidersHorizontal className="h-4 w-4" />
              列显示
            </button>
            <button type="button" className={PRIMARY_BTN} onClick={() => setModal({ kind: 'create' })}>
              <Plus className="h-4 w-4" />
              新增员工
            </button>
          </div>
        }
      >
        {/* 列隐藏 / 显示面板：选项仅来自后端列白名单；不可隐藏列禁用勾选（恒可见）。 */}
        {columnPanelOpen ? (
          <div className="mb-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
            {columnsMeta.length === 0 ? (
              <p className="text-xs text-slate-500">列白名单加载中…</p>
            ) : (
              <div className="flex flex-wrap gap-x-4 gap-y-2">
                {columnsMeta.map((col) => (
                  <label
                    key={col.key}
                    className="inline-flex items-center gap-1.5 text-xs text-slate-600"
                    title={col.hideable ? undefined : '该列恒可见，不可隐藏'}
                  >
                    <input
                      type="checkbox"
                      checked={col.hideable ? effectiveVisible.includes(col.key) : true}
                      disabled={!col.hideable}
                      onChange={() => toggleCol(col.key)}
                    />
                    {col.label}
                  </label>
                ))}
              </div>
            )}
          </div>
        ) : null}

        {/* 高级搜索 */}
        <form
          className="mb-4 grid grid-cols-1 gap-3 md:grid-cols-3 xl:grid-cols-4"
          onSubmit={applyFilters}
          data-testid="employee-filter"
        >
          <label className="text-xs text-slate-600">
            <span className="mb-1 block">关键词</span>
            <input
              className={INPUT}
              value={pendingKeyword}
              onChange={(e) => setPendingKeyword(e.target.value)}
              placeholder="姓名 / 用户名 / 岗位 / 部门（完整手机号精确匹配）"
            />
          </label>
          <label className="text-xs text-slate-600">
            <span className="mb-1 block">部门</span>
            <select
              className={INPUT}
              value={deptId}
              onChange={(e) => {
                setDeptId(e.target.value);
                setPage(1);
              }}
            >
              <option value="">全部部门</option>
              {departments.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </select>
          </label>
          <label className="text-xs text-slate-600">
            <span className="mb-1 block">角色</span>
            <select
              className={INPUT}
              value={role}
              onChange={(e) => {
                setRole(e.target.value as Role | '');
                setPage(1);
              }}
            >
              <option value="">全部角色</option>
              {FILTER_ROLES.map((r) => (
                <option key={r} value={r}>
                  {hrRoleLabel(r, roleOptions)}
                </option>
              ))}
            </select>
          </label>
          <label className="text-xs text-slate-600">
            <span className="mb-1 block">状态</span>
            <select
              className={INPUT}
              value={status}
              onChange={(e) => {
                setStatus(e.target.value as EmployeeStatus | '');
                setPage(1);
              }}
            >
              <option value="">全部状态</option>
              {/* Issue #6：筛选用 EMPLOYEE_FILTER_STATUSES（含「账户已注销」）；编辑弹窗
                  的状态下拉仍用 EMPLOYEE_STATUSES（可赋值集，不含 CANCELLED）。 */}
              {EMPLOYEE_FILTER_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {STATUS_LABEL[s]}
                </option>
              ))}
            </select>
          </label>
          <label className="text-xs text-slate-600">
            <span className="mb-1 block">排序字段</span>
            <select
              className={INPUT}
              value={sortBy}
              onChange={(e) => {
                setSortBy(e.target.value as 'createdAt' | 'name' | 'lastLoginAt');
                setPage(1);
              }}
            >
              <option value="createdAt">创建时间</option>
              <option value="name">姓名</option>
              <option value="lastLoginAt">最近登录</option>
            </select>
          </label>
          <label className="text-xs text-slate-600">
            <span className="mb-1 block">排序方向</span>
            <select
              className={INPUT}
              value={sortOrder}
              onChange={(e) => {
                setSortOrder(e.target.value as 'asc' | 'desc');
                setPage(1);
              }}
            >
              <option value="desc">降序</option>
              <option value="asc">升序</option>
            </select>
          </label>
          <div className="flex items-end gap-2">
            <button type="submit" className={PRIMARY_BTN}>
              查询
            </button>
            <button type="button" className={SECONDARY_BTN} onClick={resetFilters}>
              重置
            </button>
          </div>
        </form>

        {employeesQuery.isLoading ? (
          <LoadingState label="正在加载员工列表…" />
        ) : employeesQuery.isError ? (
          <ErrorState
            title="加载员工列表失败"
            description={errMessage(employeesQuery.error, '请稍后重试。')}
            retry={() => void employeesQuery.refetch()}
          />
        ) : employees.length === 0 ? (
          <EmptyState title="暂无员工" description="调整筛选条件，或点击右上角「新增员工」创建。" />
        ) : (
          <div className="overflow-x-auto" data-testid="employee-table">
            <table className="w-full min-w-[760px] border-collapse text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left text-xs text-slate-500">
                  {dataColumns.map((col) => (
                    <th key={col} className="py-2 pr-3">
                      {colLabel(col)}
                    </th>
                  ))}
                  <th className="py-2 pr-3 text-right">{colLabel('actions')}</th>
                </tr>
              </thead>
              <tbody>
                {employees.map((emp) => (
                  <tr key={emp.id} className="border-b border-slate-100 align-middle">
                    {dataColumns.map((col) => (
                      <td key={col} className="py-2.5 pr-3">
                        <EmployeeCell
                          col={col}
                          emp={emp}
                          fallbackAvatar={configuredDefaultAvatar}
                          roleOptions={roleOptions}
                        />
                      </td>
                    ))}
                    <td className="py-2.5 pr-3">
                      <div className="flex flex-wrap items-center justify-end gap-1.5">
                        <button className={SECONDARY_BTN} onClick={() => setModal({ kind: 'edit', employee: emp })}>
                          <Pencil className="h-3.5 w-3.5" /> 编辑
                        </button>
                        <button className={SECONDARY_BTN} onClick={() => setModal({ kind: 'assign-courses', employee: emp })}>
                          <BookOpen className="h-3.5 w-3.5" /> 课程分配
                        </button>
                        <button className={SECONDARY_BTN} onClick={() => setModal({ kind: 'reset-password', employee: emp })}>
                          <KeyRound className="h-3.5 w-3.5" /> 重置密码
                        </button>
                        <button className={SECONDARY_BTN} onClick={() => setModal({ kind: 'reset-training', employee: emp })}>
                          <RotateCcw className="h-3.5 w-3.5" /> 重置进度
                        </button>
                        <button className={SECONDARY_BTN} onClick={() => setModal({ kind: 'training-recovery', employee: emp })}>
                          <Wrench className="h-3.5 w-3.5" /> 清理卡死
                        </button>
                        <button
                          className={`${SECONDARY_BTN} text-rose-600 hover:bg-rose-50`}
                          onClick={() => setModal({ kind: 'delete', employee: emp })}
                          disabled={emp.id === currentUser?.id}
                          title={emp.id === currentUser?.id ? '不能删除自己' : undefined}
                        >
                          <Trash2 className="h-3.5 w-3.5" /> 删除
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* 分页：展示总条数 / 当前页 / 总页数，支持页大小切换与上一页 / 下一页。 */}
        {pagination ? (
          <div className="mt-4 flex flex-wrap items-center justify-between gap-2 text-xs text-slate-500">
            <div className="flex items-center gap-2">
              <span>
                共 {pagination.total} 条 · 第 {pagination.page}/{pagination.totalPages} 页
              </span>
              <select
                className="rounded-md border border-slate-300 px-1.5 py-1"
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPage(1);
                }}
                data-testid="employee-page-size"
              >
                {EMPLOYEE_PAGE_SIZE_OPTIONS.map((n) => (
                  <option key={n} value={n}>
                    {n} 条/页
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                className="inline-flex items-center gap-1 rounded-md border border-slate-300 px-2.5 py-1.5 disabled:opacity-50"
                disabled={pagination.page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                <ChevronLeft className="h-3.5 w-3.5" /> 上一页
              </button>
              <button
                type="button"
                className="inline-flex items-center gap-1 rounded-md border border-slate-300 px-2.5 py-1.5 disabled:opacity-50"
                disabled={pagination.page >= pagination.totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                下一页 <ChevronRight className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        ) : null}
      </Card>

      {modal?.kind === 'create' ? (
        <EmployeeFormModal key="create" mode="create" onClose={closeModal} onDone={refresh} />
      ) : null}
      {modal?.kind === 'edit' ? (
        <EmployeeFormModal
          key={`edit-${modal.employee.id}`}
          mode="edit"
          employee={modal.employee}
          onClose={closeModal}
          onDone={refresh}
        />
      ) : null}
      {modal?.kind === 'delete' ? (
        <DeleteEmployeeModal key={`del-${modal.employee.id}`} employee={modal.employee} onClose={closeModal} onDone={refresh} />
      ) : null}
      {modal?.kind === 'reset-password' ? (
        <ResetPasswordModal key={`rp-${modal.employee.id}`} employee={modal.employee} onClose={closeModal} />
      ) : null}
      {modal?.kind === 'reset-training' ? (
        <ResetTrainingModal key={`rt-${modal.employee.id}`} employee={modal.employee} onClose={closeModal} />
      ) : null}
      {modal?.kind === 'assign-courses' ? (
        <AssignCoursesModal key={`ac-${modal.employee.id}`} employee={modal.employee} onClose={closeModal} />
      ) : null}
      {modal?.kind === 'training-recovery' ? (
        <TrainingRecoveryModal key={`tr-${modal.employee.id}`} employee={modal.employee} onClose={closeModal} />
      ) : null}
    </div>
  );
}

function Header() {
  return (
    <header>
      <div className="text-xs text-slate-500">组织 / 员工管理</div>
      <h1 className="mt-1 text-2xl font-semibold text-slate-900">员工管理</h1>
    </header>
  );
}

function emptyForm(employee?: AdminEmployeeView): EmployeeFormValues {
  return {
    name: employee?.name ?? '',
    username: employee?.username ?? '',
    phone: '',
    password: '',
    avatarUrl: employee?.avatarUrl ?? '',
    deptId: employee?.deptId ?? '',
    jobTitle: employee?.jobTitle ?? '',
    role: (employee && employee.role !== 'SUPER_ADMIN' ? employee.role : 'STAFF') as EmployeeFormValues['role'],
    status: employee?.status ?? 'ACTIVE',
  };
}

function EmployeeFormModal({
  mode,
  employee,
  onClose,
  onDone,
}: {
  mode: 'create' | 'edit';
  employee?: AdminEmployeeView;
  onClose: () => void;
  onDone: () => void;
}) {
  const [values, setValues] = useState<EmployeeFormValues>(() => emptyForm(employee));
  const [errors, setErrors] = useState<ReturnType<typeof validateEmployeeForm>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);

  const presetsQuery = useQuery({
    queryKey: ['admin', 'avatar-presets'],
    queryFn: listAvatarPresetsApi,
    staleTime: 60 * 60 * 1000,
  });
  const departmentsQuery = useQuery({
    queryKey: ['admin', 'departments'],
    queryFn: listDepartmentsApi,
    staleTime: 5 * 60 * 1000,
  });
  const hrConfigQuery = useQuery({
    queryKey: ['system', 'hr-config'],
    queryFn: getHrConfigApi,
    staleTime: 5 * 60 * 1000,
  });

  const mutation = useMutation({
    mutationFn: () =>
      mode === 'create'
        ? createEmployeeApi(toCreatePayload(values))
        : updateEmployeeApi(employee!.id, toUpdatePayload(values)),
    onSuccess: () => {
      onDone();
      onClose();
    },
    onError: (err) => setSubmitError(errMessage(err, '保存失败，请重试。')),
  });

  const set = <K extends keyof EmployeeFormValues>(key: K, value: EmployeeFormValues[K]) =>
    setValues((prev) => ({ ...prev, [key]: value }));

  const onSubmit = () => {
    setSubmitError(null);
    const found = validateEmployeeForm(values, mode);
    setErrors(found);
    if (Object.keys(found).length > 0) return;
    mutation.mutate();
  };

  const presets = presetsQuery.data?.urls ?? [];
  // Issue #183 返修（P0-3）：「使用默认」预览 = 租户配置的默认头像（缺失回退出厂 logo）。
  const configuredDefaultUrl =
    presetsQuery.data?.configuredDefaultUrl ?? presetsQuery.data?.defaultUrl;
  const positionOptions = withCurrentPosition(
    hrConfigQuery.data?.positions,
    values.jobTitle,
  );
  const roleOptions = normalizeHrRoleOptions(hrConfigQuery.data?.roles);

  return (
    <Modal
      title={mode === 'create' ? '新增员工' : `编辑员工 · ${employee?.name ?? ''}`}
      description={mode === 'create' ? '初始密码可留空（默认使用系统默认密码）；新员工首次登录将被强制修改密码。' : '手机号留空表示不修改。'}
      onClose={onClose}
      wide
      footer={
        <div className="flex items-center justify-end gap-2">
          <button className={SECONDARY_BTN} onClick={onClose} disabled={mutation.isPending}>
            取消
          </button>
          <button className={PRIMARY_BTN} onClick={onSubmit} disabled={mutation.isPending}>
            {mutation.isPending ? '保存中…' : '保存'}
          </button>
        </div>
      }
    >
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label="真实姓名" error={errors.name} required>
          <input className={INPUT} value={values.name} onChange={(e) => set('name', e.target.value)} />
        </Field>
        <Field label="用户名 / 工号" error={errors.username}>
          <input className={INPUT} value={values.username} onChange={(e) => set('username', e.target.value)} />
        </Field>
        <Field label={mode === 'create' ? '手机号' : '手机号（留空不改）'} error={errors.phone} required={mode === 'create'}>
          <input className={INPUT} value={values.phone} onChange={(e) => set('phone', e.target.value)} placeholder="11 位手机号" />
        </Field>
        {mode === 'create' ? (
          <Field label="初始密码（留空用系统默认）" error={errors.password}>
            <input
              className={INPUT}
              type="password"
              value={values.password}
              onChange={(e) => set('password', e.target.value)}
              placeholder="留空使用系统默认密码；或填 8-64 位覆盖"
            />
          </Field>
        ) : isCancelled(values.status) ? (
          /* Issue #6：已注销是员工自助的终态——不给管理员一个「改回在职」的下拉（那会静默
             复活账户）。渲染成只读说明；其余档案字段（姓名 / 部门 / 岗位…）照常可编辑，
             toUpdatePayload 对这类员工省略 status，PATCH 不传即不改。 */
          <Field label="状态">
            <div
              data-testid="employee-status-cancelled-readonly"
              className={`inline-flex rounded-full border px-2 py-0.5 text-xs ${STATUS_TONE.CANCELLED}`}
            >
              {STATUS_LABEL.CANCELLED}
            </div>
            <p className="mt-1 text-xs text-slate-500">
              员工已自助注销账户，状态不可修改；账号无法再登录。
            </p>
          </Field>
        ) : (
          <Field label="状态" error={errors.status}>
            <select className={INPUT} value={values.status} onChange={(e) => set('status', e.target.value as EmployeeStatus)}>
              {EMPLOYEE_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {STATUS_LABEL[s]}
                </option>
              ))}
            </select>
          </Field>
        )}
        <Field label="部门">
          <select className={INPUT} value={values.deptId} onChange={(e) => set('deptId', e.target.value)}>
            <option value="">未分配</option>
            {(departmentsQuery.data ?? []).map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="岗位 / 职位" error={errors.jobTitle}>
          <select
            className={INPUT}
            value={values.jobTitle}
            onChange={(e) => set('jobTitle', e.target.value)}
          >
            <option value="">未选择</option>
            {positionOptions.map((position) => (
              <option key={position} value={position}>
                {position}
              </option>
            ))}
          </select>
        </Field>
        <Field label="角色" error={errors.role}>
          <select className={INPUT} value={values.role} onChange={(e) => set('role', e.target.value as EmployeeFormValues['role'])}>
            {roleOptions.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </select>
        </Field>
      </div>

      <div className="mt-3">
        <div className="mb-1.5 text-xs font-medium text-slate-600">头像（默认金专 logo / 预设十二生肖）</div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            className={`flex h-10 w-10 items-center justify-center overflow-hidden rounded-full border text-[10px] text-slate-500 ${values.avatarUrl === '' ? 'border-brand-500 ring-2 ring-brand-100' : 'border-slate-200'}`}
            onClick={() => set('avatarUrl', '')}
            title="使用系统默认头像（留空，由服务端落到租户配置的默认头像）"
          >
            {/* 「使用默认」预览租户配置的默认头像；配置项加载失败再兜底随包 logo。 */}
            {configuredDefaultUrl ? (
              <AvatarThumb src={configuredDefaultUrl} />
            ) : (
              <img src={jzLogo} alt="默认头像" className="h-full w-full object-cover" />
            )}
          </button>
          {presets.map((url) => (
            <button
              key={url}
              type="button"
              className={`h-10 w-10 overflow-hidden rounded-full border ${values.avatarUrl === url ? 'border-brand-500 ring-2 ring-brand-100' : 'border-slate-200'}`}
              onClick={() => set('avatarUrl', url)}
            >
              <AvatarThumb src={url} />
            </button>
          ))}
        </div>
        <p className="mt-1.5 text-xs text-slate-400">
          「使用默认」即系统设置中配置的默认头像（未配置时为金专 logo；未上传真人头像的员工默认显示）。真人头像由员工本人在移动端「我的 → 更换头像」上传；此处管理员可选择预设生肖头像。
        </p>
      </div>

      {submitError ? (
        <div className="mt-3 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">{submitError}</div>
      ) : null}
    </Modal>
  );
}

function Field({
  label,
  error,
  required,
  children,
}: {
  label: string;
  error?: string;
  required?: boolean;
  children: ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1 text-xs">
      <span className="font-medium text-slate-600">
        {label}
        {required ? <span className="text-rose-500"> *</span> : null}
      </span>
      {children}
      {error ? <span className="text-rose-600">{error}</span> : null}
    </label>
  );
}

function DeleteEmployeeModal({
  employee,
  onClose,
  onDone,
}: {
  employee: AdminEmployeeView;
  onClose: () => void;
  onDone: () => void;
}) {
  const [error, setError] = useState<string | null>(null);
  // Issue #235：删除前需通过后端签发的 6 位图形验证码。
  const captcha = useDeleteCaptcha('employee:delete');
  const [captchaCode, setCaptchaCode] = useState('');
  // Issue #329：高危二次确认——当前管理员登录密码（后端 bcrypt 校验，绝不记录/回显）。
  const [currentPassword, setCurrentPassword] = useState('');
  const mutation = useMutation({
    mutationFn: () =>
      softDeleteEmployeeApi(employee.id, {
        captchaId: captcha.captchaId ?? '',
        captchaCode,
        currentPassword,
      }),
    onSuccess: () => {
      onDone();
      onClose();
    },
    onError: (err) => {
      setError(errMessage(err, '删除失败，请重试。'));
      // 验证码 / 密码错误（400）后刷新验证码并清空两项，要求重新输入。
      if (err instanceof ApiError && err.status === 400) {
        setCaptchaCode('');
        setCurrentPassword('');
        void captcha.refresh();
      }
    },
  });
  const canDelete =
    !mutation.isPending &&
    !!captcha.captchaId &&
    captchaCode.trim().length === 6 &&
    currentPassword.length > 0;
  return (
    <Modal
      title={`软删除员工 · ${employee.name}`}
      onClose={onClose}
      footer={
        <div className="flex items-center justify-end gap-2">
          <button className={SECONDARY_BTN} onClick={onClose} disabled={mutation.isPending}>
            取消
          </button>
          <button className={DANGER_BTN} onClick={() => mutation.mutate()} disabled={!canDelete}>
            {mutation.isPending ? '处理中…' : '确认软删除'}
          </button>
        </div>
      }
    >
      <p className="text-sm text-slate-600">
        软删除后该员工无法登录，但其质检 / 学习 / 答题 / 审计历史完整保留，手机号可被复用。此操作不会物理删除任何历史数据。
      </p>
      <DeleteCaptchaField
        svg={captcha.svg}
        loading={captcha.loading}
        error={captcha.error}
        value={captchaCode}
        onChange={setCaptchaCode}
        onRefresh={captcha.refresh}
      />
      {/* Issue #329：当前管理员登录密码二次确认。后端强制校验，前端不能绕过；绝不回显已输入密码。 */}
      <div className="mt-4">
        <label className="mb-1 block text-sm font-medium text-slate-700" htmlFor="delete-current-password">
          当前管理员登录密码
        </label>
        <input
          id="delete-current-password"
          type="password"
          autoComplete="current-password"
          className="block w-full rounded-md border border-slate-300 px-3 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100"
          value={currentPassword}
          onChange={(e) => setCurrentPassword(e.target.value)}
          placeholder="请输入你的登录密码以确认删除"
          data-testid="delete-employee-password"
        />
        <p className="mt-1 text-xs text-slate-400">高危操作需二次确认；管理员账户不可删除。系统不会保存或显示该密码。</p>
      </div>
      {error ? <div className="mt-3 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">{error}</div> : null}
    </Modal>
  );
}

// Issue #400：重置改为随机临时密码——成功后一次性展示 tempPassword（后端只存哈希，
// 关闭弹窗后不可再查询），管理员需当场转告员工；员工首次登录强制改密。
function ResetPasswordModal({ employee, onClose }: { employee: AdminEmployeeView; onClose: () => void }) {
  const [error, setError] = useState<string | null>(null);
  const [tempPassword, setTempPassword] = useState<string | null>(null);
  const mutation = useMutation({
    mutationFn: () => resetEmployeePasswordApi(employee.id),
    onSuccess: (res) => setTempPassword(res.tempPassword),
    onError: (err) => setError(errMessage(err, '重置失败，请重试。')),
  });
  const done = tempPassword !== null;
  return (
    <Modal
      title={`重置密码 · ${employee.name}`}
      onClose={onClose}
      footer={
        <div className="flex items-center justify-end gap-2">
          <button className={SECONDARY_BTN} onClick={onClose}>
            {done ? '关闭' : '取消'}
          </button>
          {!done ? (
            <button className={PRIMARY_BTN} onClick={() => mutation.mutate()} disabled={mutation.isPending}>
              {mutation.isPending ? '处理中…' : '重置为随机临时密码'}
            </button>
          ) : null}
        </div>
      }
    >
      {done ? (
        <div className="space-y-3">
          <p className="text-sm text-emerald-700">
            已重置。请把下方一次性临时密码转告员工；关闭本窗口后将无法再次查看（系统只保存加密哈希）。
          </p>
          <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-center font-mono text-lg tracking-widest text-slate-900">
            {tempPassword}
          </div>
          <p className="text-xs text-slate-500">员工用临时密码登录后将被强制修改密码；如该账号此前因登录失败被临时锁定，本次重置已同步解锁。</p>
        </div>
      ) : (
        <p className="text-sm text-slate-600">
          确认重置该员工密码？系统将随机生成一次性临时密码并仅在本窗口显示一次，重置后员工首次登录必须修改密码。
        </p>
      )}
      {error ? <div className="mt-3 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">{error}</div> : null}
    </Modal>
  );
}

// Issue #184：员工课程分配弹窗。可勾选一个或多个课程，保存走真实
// PUT /admin/employees/:id/courses（覆盖式），默认勾选「企业文化」由后端建号时分配。
function AssignCoursesModal({ employee, onClose }: { employee: AdminEmployeeView; onClose: () => void }) {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [hydrated, setHydrated] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const assignmentQuery = useQuery({
    queryKey: ['admin', 'employee-courses', employee.id],
    queryFn: () => getEmployeeCoursesApi(employee.id),
  });

  // 首次拿到后端分配后回填勾选状态（key 重挂载保证不串号）。
  useEffect(() => {
    if (assignmentQuery.data && !hydrated) {
      setSelected(new Set(assignmentQuery.data.assignedCourseIds));
      setHydrated(true);
    }
  }, [assignmentQuery.data, hydrated]);

  const mutation = useMutation({
    mutationFn: () => setEmployeeCoursesApi(employee.id, [...selected]),
    onSuccess: () => setDone(true),
    onError: (err) => setError(errMessage(err, '保存课程分配失败，请重试。')),
  });

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const courses = assignmentQuery.data?.courses ?? [];

  return (
    <Modal
      title={`课程分配 · ${employee.name}`}
      description="勾选要分配给该员工的课程（可多选）。保存即写入后端，刷新后保持。"
      onClose={onClose}
      wide
      footer={
        <div className="flex items-center justify-end gap-2">
          <button className={SECONDARY_BTN} onClick={onClose} disabled={mutation.isPending}>
            {done ? '关闭' : '取消'}
          </button>
          {!done ? (
            <button
              className={PRIMARY_BTN}
              onClick={() => {
                setError(null);
                mutation.mutate();
              }}
              disabled={mutation.isPending || assignmentQuery.isLoading}
            >
              {mutation.isPending ? '保存中…' : `保存（已选 ${selected.size}）`}
            </button>
          ) : null}
        </div>
      }
    >
      {assignmentQuery.isLoading ? (
        <LoadingState label="正在加载课程分配…" />
      ) : assignmentQuery.isError ? (
        <ErrorState
          title="加载课程分配失败"
          description={errMessage(assignmentQuery.error, '请稍后重试。')}
          retry={() => void assignmentQuery.refetch()}
        />
      ) : courses.length === 0 ? (
        <EmptyState
          title="暂无可分配课程"
          description="请先在课程管理中创建并启用课程（如企业文化、细胞基础知识）。"
        />
      ) : done ? (
        <p className="text-sm text-emerald-700">
          已保存课程分配，共 {selected.size} 门课程。刷新页面仍保持。
        </p>
      ) : (
        <div className="flex flex-col gap-1.5">
          {courses.map((c) => {
            const checked = selected.has(c.id);
            return (
              <label
                key={c.id}
                className={`flex cursor-pointer items-center gap-2.5 rounded-md border px-3 py-2 text-sm ${checked ? 'border-brand-300 bg-brand-50' : 'border-slate-200'}`}
              >
                <input
                  type="checkbox"
                  className="h-4 w-4"
                  checked={checked}
                  onChange={() => toggle(c.id)}
                />
                <span className="font-medium text-slate-900">{c.name}</span>
                <span className="text-xs text-slate-400">{c.code}</span>
                {c.status === 'DISABLED' ? (
                  <span className="ml-auto rounded-full border border-slate-200 bg-slate-100 px-2 py-0.5 text-xs text-slate-500">
                    已停用
                  </span>
                ) : null}
              </label>
            );
          })}
        </div>
      )}
      {error ? (
        <div className="mt-3 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">{error}</div>
      ) : null}
    </Modal>
  );
}

function ResetTrainingModal({ employee, onClose }: { employee: AdminEmployeeView; onClose: () => void }) {
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{ plansReset: number } | null>(null);
  const mutation = useMutation({
    mutationFn: () => resetEmployeeTrainingApi(employee.id),
    onSuccess: (data) => setResult({ plansReset: data.plansReset }),
    onError: (err) => setError(errMessage(err, '重置失败，请重试。')),
  });
  return (
    <Modal
      title={`重置学习进度 · ${employee.name}`}
      onClose={onClose}
      footer={
        <div className="flex items-center justify-end gap-2">
          <button className={SECONDARY_BTN} onClick={onClose}>
            {result ? '关闭' : '取消'}
          </button>
          {!result ? (
            <button className={PRIMARY_BTN} onClick={() => mutation.mutate()} disabled={mutation.isPending}>
              {mutation.isPending ? '处理中…' : '确认重置全部课程'}
            </button>
          ) : null}
        </div>
      }
    >
      {result ? (
        <p className="text-sm text-emerald-700">
          已为该员工重置 {result.plansReset} 个课程的学习进度并开启新一轮。旧轮答题明细完整保留，可在历史回顾中查看。
        </p>
      ) : (
        <p className="text-sm text-slate-600">
          确认代该员工重置全部课程的学习进度？将为有进度的课程开启全新一轮，旧轮 record 与答题明细保留供只读历史回顾，不会删除任何历史。
        </p>
      )}
      {error ? <div className="mt-3 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">{error}</div> : null}
    </Modal>
  );
}

function DistributionTable({ title, distribution }: { title: string; distribution: Record<string, number> }) {
  const rows = statusDistributionRows(distribution);
  return (
    <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
      <p className="text-xs font-medium text-slate-500">{title}</p>
      {rows.length ? (
        <ul className="mt-1 flex flex-wrap gap-x-4 gap-y-1">
          {rows.map((row) => (
            <li key={row.status} className="text-xs text-slate-700">
              <span className="font-mono">{row.status}</span> · {row.count}
            </li>
          ))}
        </ul>
      ) : (
        <p className="mt-1 text-xs text-slate-400">无</p>
      )}
    </div>
  );
}

function TrainingRecoveryModal({ employee, onClose }: { employee: AdminEmployeeView; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [applyError, setApplyError] = useState<string | null>(null);
  const [applied, setApplied] = useState<TrainingRecoveryApplyResult | null>(null);

  const dryRunQuery = useQuery({
    queryKey: ['training-recovery-dry-run', employee.id],
    queryFn: () => trainingRecoveryDryRunApi(employee.id),
    refetchOnWindowFocus: false,
  });

  const applyMutation = useMutation({
    mutationFn: () => trainingRecoveryApplyApi(employee.id),
    onSuccess: (data) => {
      setApplied(data);
      setApplyError(null);
      void queryClient.invalidateQueries({ queryKey: ['training-recovery-dry-run', employee.id] });
    },
    onError: (err) => setApplyError(errMessage(err, '清理失败，请重试。')),
  });

  const dryRun = dryRunQuery.data;
  const actionable = dryRun ? hasActionableRecovery(dryRun.plannedActions) : false;

  return (
    <Modal
      title={`清理 SOP 卡死状态 · ${employee.name}`}
      description="按 orgId + 员工强制隔离的只读诊断与受控清理：AI 已完成→同步答题为 COMPLETED；超时未完成才落 TIMEOUT/FAILED；已终态幂等跳过。仅展示脱敏计数。"
      onClose={onClose}
      wide
      footer={
        <div className="flex items-center justify-end gap-2">
          <button className={SECONDARY_BTN} onClick={onClose}>
            {applied ? '关闭' : '取消'}
          </button>
          {!applied ? (
            <button
              className={PRIMARY_BTN}
              onClick={() => applyMutation.mutate()}
              disabled={!actionable || applyMutation.isPending || dryRunQuery.isPending}
            >
              {applyMutation.isPending ? '清理中…' : actionable ? '确认清理卡死状态' : '无可清理项'}
            </button>
          ) : null}
        </div>
      }
    >
      {dryRunQuery.isPending ? (
        <p className="text-sm text-slate-500">正在诊断该员工的卡死状态…</p>
      ) : dryRunQuery.isError ? (
        <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
          {errMessage(dryRunQuery.error, '诊断失败，请重试。')}
        </div>
      ) : dryRun ? (
        <div className="space-y-3">
          <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-slate-700">
            <span>
              命中训练记录：<strong>{dryRun.trainingRecordsMatched}</strong>
            </span>
            <span>
              卡死答题：<strong>{dryRun.stuckAnswers}</strong>
            </span>
          </div>
          <DistributionTable title="答题状态分布" distribution={dryRun.answerStatusDistribution} />
          <DistributionTable title="AI 任务状态分布" distribution={dryRun.aiTaskStatusDistribution} />
          <DistributionTable title="ASR 任务状态分布" distribution={dryRun.asrTaskStatusDistribution} />
          <div className="rounded-md border border-slate-200 px-3 py-2">
            <p className="text-xs font-medium text-slate-500">{applied ? '已执行动作' : '将执行动作'}</p>
            <p className="mt-1 text-sm text-slate-700">
              {describeRecoverySummary(applied ? applied.appliedActions : dryRun.plannedActions)}
            </p>
          </div>
          {applied ? (
            <p className="text-sm text-emerald-700">
              清理完成，本次更新 {applied.answersUpdated} 条答题。已重新诊断验证幂等收敛。
            </p>
          ) : !actionable ? (
            <p className="text-sm text-slate-500">当前无卡死项可清理（均为有效处理中或已终态）。</p>
          ) : null}
        </div>
      ) : null}
      {applyError ? (
        <div className="mt-3 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
          {applyError}
        </div>
      ) : null}
    </Modal>
  );
}
